// src/components/dashboard/groups/InviteModal.tsx

import React, { useState } from 'react';
import { X, Copy, Check, Link, Hash, MessageCircle, Send, RefreshCw } from 'lucide-react';
import './InviteModal.css';

interface InviteModalProps {
    isOpen: boolean;
    groupName: string;
    inviteCode: string;
    onClose: () => void;
    onRegenerateCode: () => Promise<void>;
    isAdmin: boolean;
}

const BASE_URL = 'https://www.graia.com.br';

const InviteModal: React.FC<InviteModalProps> = ({
    isOpen,
    groupName,
    inviteCode,
    onClose,
    onRegenerateCode,
    isAdmin,
}) => {
    const [copiedLink, setCopiedLink] = useState(false);
    const [copiedCode, setCopiedCode] = useState(false);
    const [regenerating, setRegenerating] = useState(false);

    if (!isOpen) return null;

    const inviteLink = `${BASE_URL}/invite/${inviteCode}`;

    const whatsappText = `Olá! Você foi convidado para entrar no grupo de estudos *${groupName}* na plataforma Graia.\n\nClique no link para entrar:\n${inviteLink}`;
    const telegramText = `Venha estudar no grupo ${groupName} na plataforma Graia!`;

    const copyToClipboard = async (text: string, type: 'link' | 'code') => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'link') {
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 2000);
            } else {
                setCopiedCode(true);
                setTimeout(() => setCopiedCode(false), 2000);
            }
        } catch {
            // fallback silencioso
        }
    };

    const handleRegenerate = async () => {
        setRegenerating(true);
        try {
            await onRegenerateCode();
        } finally {
            setRegenerating(false);
        }
    };

    return (
        <div className="invite-overlay" onClick={onClose}>
            <div className="invite-modal" onClick={(e) => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="invite-modal__header">
                    <h2 className="invite-modal__title">Convidar membros</h2>
                    <button className="invite-modal__close" onClick={onClose} aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                {/* ── Link de convite ── */}
                <div className="invite-modal__section">
                    <span className="invite-modal__section-label">
                        <Link size={13} />
                        Link de convite
                    </span>
                    <div className="invite-modal__copy-row">
                        <span className="invite-modal__copy-value">{inviteLink}</span>
                        <button
                            className={`invite-modal__btn-copy${copiedLink ? ' invite-modal__btn-copy--copied' : ''}`}
                            onClick={() => copyToClipboard(inviteLink, 'link')}
                        >
                            {copiedLink ? <Check size={14} /> : <Copy size={14} />}
                            {copiedLink ? 'Copiado' : 'Copiar'}
                        </button>
                    </div>
                </div>

                <div className="invite-modal__divider" />

                {/* ── Código do grupo ── */}
                <div className="invite-modal__section">
                    <span className="invite-modal__section-label">
                        <Hash size={13} />
                        Código do grupo
                    </span>
                    <div className="invite-modal__copy-row">
                        <span className="invite-modal__copy-value invite-modal__copy-value--code">
                            {inviteCode}
                        </span>
                        <button
                            className={`invite-modal__btn-copy${copiedCode ? ' invite-modal__btn-copy--copied' : ''}`}
                            onClick={() => copyToClipboard(inviteCode, 'code')}
                        >
                            {copiedCode ? <Check size={14} /> : <Copy size={14} />}
                            {copiedCode ? 'Copiado' : 'Copiar'}
                        </button>
                    </div>

                    {isAdmin && (
                        <button
                            className="invite-modal__btn-regenerate"
                            onClick={handleRegenerate}
                            disabled={regenerating}
                        >
                            <RefreshCw
                                size={13}
                                className={regenerating ? 'invite-modal__spin' : ''}
                            />
                            {regenerating ? 'Gerando...' : 'Gerar novo código'}
                        </button>
                    )}
                </div>

                <div className="invite-modal__divider" />

                {/* ── Compartilhar ── */}
                <div className="invite-modal__section">
                    <span className="invite-modal__section-label">Compartilhar</span>
                    <div className="invite-modal__share-row">
                        <a
                            href={`https://wa.me/?text=${encodeURIComponent(whatsappText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="invite-modal__share-btn"
                        >
                            <MessageCircle size={18} color="#25D366" />
                            WhatsApp
                        </a>
                        <a
                            href={`https://t.me/share/url?url=${encodeURIComponent(inviteLink)}&text=${encodeURIComponent(telegramText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="invite-modal__share-btn"
                        >
                            <Send size={18} color="#229ED9" />
                            Telegram
                        </a>
                        <button
                            className="invite-modal__share-btn"
                            onClick={() => copyToClipboard(inviteLink, 'link')}
                        >
                            <Copy size={18} />
                            Copiar link
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default InviteModal;